// SchrankWall - Rendert eine Schrankwand (mehrere Fächer mit gemeinsamer wand_id)
// als zusammenhängender Holzkorpus mit Tür-Fächern.
//
// Alleinstehende Schränke (wand_id = null) werden einzeln über <SchrankDoor> gerendert.
import React from 'react';
import { SchrankDoor } from './SchrankDoor';

const TYP_WOOD_COLORS = {
  PERSOENLICH: { top: '#a16937', mid: '#8b5a2b', bot: '#704826', grain: '#5d3a1c' },
  WASCHRAUM:   { top: '#c89b76', mid: '#a87a55', bot: '#86603f', grain: '#634528' },
  ESSENSRAUM:  { top: '#9a6a3f', mid: '#7a4f2a', bot: '#5d3a1c', grain: '#3e2614' },
  GEMEINSCHAFT:{ top: '#b48157', mid: '#945e3a', bot: '#6e4524', grain: '#4a2e16' },
  CUSTOM:      { top: '#b4895c', mid: '#946838', bot: '#704a23', grain: '#4f2f14' },
};

// Gruppiert Schränke nach wand_id. Container und Solo-Schränke werden einzeln behandelt.
// Fächer (wand_id != null UND !ist_container) bilden Gruppen.
export function groupSchraenkeByWand(schraenke) {
  const groups = new Map();
  schraenke.forEach((s) => {
    if (s.ist_container || !s.wand_id) {
      // Container oder Einzelschrank → eigene "Gruppe"
      groups.set(`solo-${s.id}`, [s]);
    } else {
      const key = s.wand_id;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(s);
    }
  });
  return Array.from(groups.entries()).map(([wandId, items]) => ({
    wandId,
    items,
    isWall: items.length > 1 && !wandId.startsWith('solo-'),
  }));
}

function boundingBox(items) {
  if (!items.length) return { x: 0, y: 0, width: 0, height: 0 };
  const xs = items.map(s => s.geometry.x);
  const ys = items.map(s => s.geometry.y);
  const xe = items.map(s => s.geometry.x + s.geometry.width);
  const ye = items.map(s => s.geometry.y + s.geometry.height);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, width: Math.max(...xe) - x, height: Math.max(...ye) - y };
}

// Schrankwand mit gemeinsamem Korpus und kleinen Tür-Fächern
function SchrankWallGroup({ items, onSchrankClick, onSchrankHoverEnter, onSchrankHoverLeave, highlightSet, selectedId }) {
  const first = items[0];
  const dir = first.tuer_richtung || 'SUED';
  const wood = TYP_WOOD_COLORS[first.typ] || TYP_WOOD_COLORS.PERSOENLICH;
  const bb = boundingBox(items);
  const horizontal = dir === 'SUED' || dir === 'NORD';

  // Sortiere Fächer für visuelle Tab-Order
  const sorted = [...items].sort((a, b) => {
    if (Math.abs(a.geometry.y - b.geometry.y) > 5) return a.geometry.y - b.geometry.y;
    return a.geometry.x - b.geometry.x;
  });

  const gradId = `wall-${first.wand_id}-grad`;

  return (
    <g data-testid={`schrank-wall-${first.wand_id}`}>
      <defs>
        <linearGradient id={gradId}
                        x1="0%" y1="0%"
                        x2={horizontal ? '0%' : '100%'}
                        y2={horizontal ? '100%' : '0%'}>
          <stop offset="0%" stopColor={wood.top} />
          <stop offset="50%" stopColor={wood.mid} />
          <stop offset="100%" stopColor={wood.bot} />
        </linearGradient>
      </defs>
      {/* Schatten unter der Wand */}
      <rect x={bb.x + 2} y={bb.y + 3}
            width={bb.width} height={bb.height}
            fill="rgba(0,0,0,0.25)" rx={3} />
      {/* Massiver Wand-Korpus */}
      <rect x={bb.x - 2} y={bb.y - 2}
            width={bb.width + 4} height={bb.height + 4}
            fill={`url(#${gradId})`}
            stroke="#3a2010" strokeWidth={1.5}
            rx={3} />
      {/* Innen-Linien (Trennstege zwischen Fächern) */}
      {sorted.map((s) => {
        const g = s.geometry;
        return (
          <rect
            key={`bg-${s.id}`}
            x={g.x} y={g.y}
            width={g.width} height={g.height}
            fill={`url(#${gradId})`}
            stroke="rgba(0,0,0,0.5)"
            strokeWidth={0.6}
          />
        );
      })}
      {/* Einzelne Fächer als kompakte SchrankDoor */}
      {sorted.map((s) => (
        <SchrankDoor
          key={s.id}
          schrank={s}
          geometry={s.geometry}
          selected={s.id === selectedId}
          onClick={onSchrankClick}
          onHoverEnter={onSchrankHoverEnter}
          onHoverLeave={onSchrankHoverLeave}
          highlighted={highlightSet ? highlightSet.has(s.id) : undefined}
          compact
        />
      ))}
    </g>
  );
}

export function SchrankGroupRenderer({ schraenke, onSchrankClick, onSchrankHoverEnter, onSchrankHoverLeave, highlightSet, selectedId }) {
  const groups = React.useMemo(() => groupSchraenkeByWand(schraenke), [schraenke]);
  return (
    <>
      {groups.map((g) =>
        g.isWall ? (
          <SchrankWallGroup
            key={g.wandId}
            items={g.items}
            onSchrankClick={onSchrankClick}
            onSchrankHoverEnter={onSchrankHoverEnter}
            onSchrankHoverLeave={onSchrankHoverLeave}
            highlightSet={highlightSet}
            selectedId={selectedId}
          />
        ) : (
          g.items.map((s) => (
            <SchrankDoor
              key={s.id}
              schrank={s}
              geometry={s.geometry}
              selected={s.id === selectedId}
              onClick={onSchrankClick}
              onHoverEnter={onSchrankHoverEnter}
              onHoverLeave={onSchrankHoverLeave}
              highlighted={highlightSet ? highlightSet.has(s.id) : undefined}
            />
          ))
        )
      )}
    </>
  );
}

export default SchrankGroupRenderer;
